
"use client";

import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { type SiteData, AISiteCreationData, GeneratedTextContent, GatherSiteInfoInput, GatherSiteInfoOutput, TemplateCardData } from '@/lib/types';
import { gatherSiteInfo } from '@/ai/flows/gather-site-info-flow';
import { generateSiteContent } from '@/ai/flows/generate-site-content-flow';
import { parseAddress } from '@/ai/flows/parse-address-flow';
import { fetchImagesForSiteAction } from '@/lib/actions/image-search-actions';
import { toast } from '@/hooks/use-toast';
import { MessageSquare, Send, User, Bot, Loader2, Wand2, Rocket, PartyPopper } from 'lucide-react';
import Image from 'next/image';
import { useAuth } from '@/contexts/auth-context';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';

type ChatMessage = {
  id: string;
  role: 'user' | 'model';
  content: string;
};

type GenerationStep = 'idle' | 'gathering' | 'confirming' | 'generating' | 'finished' | 'error';

interface AICreationChatProps {
  onFinish: (data: Partial<SiteData>, templateId: string) => void;
  initialTemplateId?: string;
  templates: ({ id: string } & TemplateCardData)[];
  isSubmitting?: boolean;
}

export function AICreationChat({ onFinish, initialTemplateId, templates, isSubmitting = false }: AICreationChatProps) {
  const { currentUser } = useAuth();
  const [step, setStep] = useState(initialTemplateId ? 2 : 1);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(initialTemplateId || null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [userInput, setUserInput] = useState('');
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [generationStep, setGenerationStep] = useState<GenerationStep>('idle');
  const [collectedData, setCollectedData] = useState<Partial<AISiteCreationData>>({});
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationMessage, setGenerationMessage] = useState('');
  const [finalProcessedData, setFinalProcessedData] = useState<Partial<SiteData> | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const chatCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialTemplateId && chatMessages.length === 0 && !isLoadingAI) {
        startAIChat(initialTemplateId);
    }
  }, [initialTemplateId]);

  useEffect(() => {
    // Scroll interno das mensagens
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    
    // No mobile, se o chat estiver ativo e houver novas mensagens, garantimos que o card está bem posicionado
    if (window.innerWidth < 768 && chatMessages.length > 0 && generationStep === 'gathering') {
        setTimeout(() => {
            chatCardRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
        }, 100);
    }
  }, [chatMessages, generationMessage, generationStep]);

  useEffect(() => {
    if (!isLoadingAI && generationStep === 'gathering' && chatInputRef.current) {
        // Impede o navegador de rolar bruscamente ao focar o input no mobile
        chatInputRef.current.focus({ preventScroll: true });
        
        // Executa uma rolagem suave e controlada para alinhar a base do assistente
        if (window.innerWidth < 768) {
            setTimeout(() => {
                chatCardRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
            }, 150);
        }
    }
  }, [isLoadingAI, generationStep, chatMessages]);

  const startAIChat = async (templateId: string) => {
    setIsLoadingAI(true);
    setChatMessages([]);
    setCollectedData({});
    setGenerationStep('gathering');
    try {
      const initialAIInput: GatherSiteInfoInput = { templateId: templateId as any };
      const initialAIResult = await gatherSiteInfo(initialAIInput);
      if (initialAIResult.aiResponseToUser) {
        setChatMessages([{ id: crypto.randomUUID(), role: 'model', content: initialAIResult.aiResponseToUser }]);
      }
    } catch (error) {
      toast({ title: "Erro de Comunicação", description: "Falha ao iniciar chat. Tente atualizar a página.", variant: "destructive" });
    } finally {
      setIsLoadingAI(false);
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setStep(2);
    startAIChat(templateId);
  };

  const handleSendMessage = async () => {
    if (!userInput.trim() || isLoadingAI || !selectedTemplateId || generationStep !== 'gathering') return;

    const newUserMessage: ChatMessage = { id: crypto.randomUUID(), role: 'user', content: userInput };
    const updatedChatMessages = [...chatMessages, newUserMessage];
    setChatMessages(updatedChatMessages);
    setUserInput('');
    setIsLoadingAI(true);

    try {
      const aiInput: GatherSiteInfoInput = {
        templateId: selectedTemplateId as any,
        conversationHistory: updatedChatMessages.map(m => ({ role: m.role, content: m.content })),
        extractedData: collectedData,
      };
      const aiResult = await gatherSiteInfo(aiInput);

      if (aiResult.aiResponseToUser) {
        setChatMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'model', content: aiResult.aiResponseToUser }]);
      }
      if (aiResult.extractedData) {
         setCollectedData(aiResult.extractedData);
      }
      if (aiResult.isGatheringComplete) {
          setGenerationStep('confirming');
      }
    } catch (error) {
      toast({ title: "Erro na IA", description: "O assistente está sobrecarregado. Tente enviar sua mensagem novamente em alguns segundos.", variant: "destructive" });
    } finally {
      setIsLoadingAI(false);
    }
  };

  const runCreativeGeneration = async () => {
    setGenerationStep('generating');
    setGenerationProgress(5);
    setGenerationMessage("A IA está iniciando o processo criativo...");
    
    await new Promise(resolve => setTimeout(resolve, 2000));

    try {
        setGenerationMessage("Escrevendo os textos e definindo as cores...");
        const content = await generateSiteContent(collectedData as AISiteCreationData);
        setGenerationProgress(30);

        setGenerationMessage("Analisando seu endereço para o mapa...");
        let parsedAddressData = {};
        if (collectedData.address && collectedData.address.toLowerCase() !== 'não informado') {
          try {
            parsedAddressData = await parseAddress({ fullAddress: collectedData.address });
          } catch (e) {
            console.error("Erro ao processar endereço:", e);
          }
        }
        setGenerationProgress(50);
        
        const baseData: Partial<SiteData> = { 
            ...collectedData, 
            ...content,
            ...parsedAddressData,
            siteName: content.siteName || collectedData.companyName || 'Meu Super Site',
            companyName: collectedData.companyName || content.siteName,
            address: '', 
        };

        if (collectedData.address && collectedData.address !== 'Não informado') {
          baseData.block10_column1_text = `<p><strong>Nosso Endereço:</strong><br/>${collectedData.address.replace(/\n/g, '<br/>')}</p><p><strong>Horário de Funcionamento:</strong><br/>Segunda a Sexta: 09:00 - 18:00<br/>Sábado: 09:00 - 12:00</p>`;
        }

        const contactButton = { text: 'Entre em Contato', link: '#contato' };
        baseData.block3_actionButton = contactButton;
        baseData.block4_actionButton = contactButton;
        baseData.block_diff_actionButton = contactButton;
        baseData.block5_actionButton = contactButton;
        baseData.block7_actionButton = contactButton;
        baseData.block9_actionButton = contactButton;
        baseData.block2_buttons = [
            { text: 'Nossos Serviços', link: '#servicos' },
            { text: 'Entre em Contato', link: '#contato' }
        ];

        setGenerationMessage("Buscando as melhores fotos para o seu ramo...");
        const dataWithImages = await fetchImagesForSiteAction(baseData);
        
        const finalData = { 
          ...baseData, 
          ...dataWithImages,
          isPublished: true 
        };

        setGenerationMessage("Tudo pronto! Seu site ficou incrível.");
        setGenerationProgress(100);
        setFinalProcessedData(finalData);
        setGenerationStep('finished');
    } catch (e: any) {
        console.error("Erro na geração:", e);
        setGenerationStep('error');
        toast({ title: "Falha na Montagem", description: "Não conseguimos gerar o site devido ao alto tráfego. Por favor, tente clicar em 'Gerar' novamente.", variant: "destructive" });
    }
  };

  const handleCompleteCreation = () => {
      if (finalProcessedData && selectedTemplateId) {
          onFinish(finalProcessedData, selectedTemplateId);
      }
  };

  if (step === 1) {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold font-headline">Escolha um estilo base</h2>
            <p className="text-muted-foreground">O assistente adaptará o conteúdo para você.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {templates.map((template) => (
            <Card key={template.id} className="overflow-hidden hover:ring-2 ring-primary transition-all cursor-pointer group" onClick={() => handleTemplateSelect(template.id)}>
              <div className="relative aspect-video">
                <Image src={template.imageUrl || 'https://placehold.co/600x400.png'} alt={template.name} fill className="object-cover group-hover:scale-105 transition-transform" />
              </div>
              <CardHeader className="p-4">
                <CardTitle className="text-lg">{template.name}</CardTitle>
                <CardDescription className="text-xs">{template.description}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <Card ref={chatCardRef} className="flex flex-col h-[450px] sm:h-[550px] border-primary/20 shadow-xl overflow-hidden bg-background/95 backdrop-blur transition-all duration-300">
      <CardHeader className="border-b bg-muted/50 p-4">
        <CardTitle className="flex items-center gap-2 text-lg font-headline">
          <MessageSquare className="text-primary h-5 w-5"/>
          Assistente WebSite App
        </CardTitle>
      </CardHeader>
      
      <CardContent className="flex-grow p-0 overflow-hidden">
        <ScrollArea className="h-full p-4">
          <div className="space-y-4">
            <div className="flex justify-start">
                <div className="bg-muted p-3 rounded-lg max-w-[85%] flex gap-2">
                    <Bot className="h-5 w-5 text-primary shrink-0 mt-1" />
                    <p className="text-sm">Olá! Sou seu assistente criativo. Para começarmos seu Super Site, me diga o nome do seu negócio.</p>
                </div>
            </div>
            
            {chatMessages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`p-3 rounded-lg max-w-[85%] flex items-start gap-2 ${msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                  {msg.role === 'model' && <Bot className="h-5 w-5 text-primary shrink-0 mt-1" />}
                  <div className="text-sm whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: msg.content }} />
                  {msg.role === 'user' && <User className="h-5 w-5 text-primary-foreground shrink-0 mt-1" />}
                </div>
              </div>
            ))}

            {generationStep === 'generating' && (
               <div className="flex justify-start">
                 <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 flex flex-col gap-3 w-full max-w-[85%]">
                   <div className="flex items-center gap-2">
                     <Loader2 className="h-5 w-5 text-primary animate-spin" />
                     <p className="font-semibold text-sm">Construindo seu Super Site...</p>
                   </div>
                   <p className="text-xs text-muted-foreground">{generationMessage}</p>
                   <Progress value={generationProgress} className="h-1" />
                 </div>
               </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </CardContent>

      <CardFooter className="border-t p-4 bg-muted/30">
        {generationStep === 'gathering' && (
          <form onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }} className="flex w-full items-center gap-2">
            <Input
              ref={chatInputRef}
              placeholder="Digite sua resposta..."
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              disabled={isLoadingAI}
            />
            <Button type="submit" disabled={isLoadingAI || !userInput.trim()} size="icon">
              {isLoadingAI ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            </Button>
          </form>
        )}

        {generationStep === 'confirming' && (
          <Button onClick={runCreativeGeneration} className="w-full bg-green-600 hover:bg-green-700 animate-pulse">
            <Wand2 className="mr-2 h-4 w-4"/> Sim, gerar meu site agora!
          </Button>
        )}

        {generationStep === 'error' && (
             <Button onClick={runCreativeGeneration} variant="destructive" className="w-full">
                <AlertTriangle className="mr-2 h-4 w-4"/> Ocorreu um erro. Tentar Gerar Novamente
            </Button>
        )}

        {generationStep === 'finished' && (
          <div className="w-full text-center space-y-3">
            <div className="flex items-center justify-center gap-2 text-green-600 font-bold">
                <PartyPopper className="h-5 w-5"/>
                <span>Site criado com sucesso!</span>
            </div>
            <Button onClick={handleCompleteCreation} disabled={isSubmitting} className="w-full h-12 text-lg font-bold">
                <Rocket className="mr-2 h-5 w-5" />
                VER MEU SUPER SITE AGORA!
            </Button>
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
